import { Link, NavLink, useNavigate } from "react-router-dom";
import { Plane, Train, Bus, Building2, Headphones, Tag, LogOut, Briefcase, ChevronDown, LayoutDashboard, Palmtree, Users, MapPin, Phone, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { login } from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export const Logo = () => (
  <Link to="/" className="flex items-center gap-1.5 sm:gap-2 shrink-0" data-testid="logo-link" aria-label="WanderNest Tour and Travel">
    <img src="/logo-mark.png" alt="" className="h-6 sm:h-9 w-auto" />
    <img src="/logo-wordmark.png" alt="WanderNest Tour and Travel" className="h-6 sm:h-10 w-auto" />
  </Link>
);

const NAV = [
  { to: "/", label: "Flights", icon: Plane, id: "flights" },
  { to: "/?tab=trains", label: "Trains", icon: Train, id: "trains" },
  { to: "/?tab=buses", label: "Buses", icon: Bus, id: "buses" },
  { to: "/holidays", label: "Holidays", icon: Palmtree, id: "holidays" },
  { to: "/?tab=hotels", label: "Hotels", icon: Building2, id: "hotels" },
  { to: "/group-booking", label: "Group booking", icon: Users, id: "group" },
];

export const Header = () => {
  const { user, logout, config } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 h-16 flex items-center gap-3 sm:gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} data-testid={`nav-${n.id}`}
              className="flex items-center gap-1.5 px-2.5 xl:px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap text-slate-600 hover:bg-orange-50 hover:text-[#EC5B24] transition-colors">
              <n.icon className="h-4 w-4 hidden xl:block" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <Link to="/holidays" data-testid="mobile-holidays-link" aria-label="Holidays" className="lg:hidden h-9 w-9 grid place-items-center rounded-full bg-slate-100 text-slate-600"><Palmtree className="h-4 w-4" /></Link>
          <Link to="/group-booking" data-testid="mobile-group-link" aria-label="Group booking" className="lg:hidden h-9 w-9 grid place-items-center rounded-full bg-slate-100 text-slate-600"><Users className="h-4 w-4" /></Link>
          <a href={`tel:+91${config.support_phone || "7719264029"}`} data-testid="mobile-call-link" aria-label="Call us" className="2xl:hidden h-9 w-9 grid place-items-center rounded-full bg-slate-100 text-slate-600"><Phone className="h-4 w-4" /></a>
          <a href="/#offers" data-testid="nav-offers" className="hidden 2xl:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#EC5B24]">
            <Tag className="h-4 w-4" /> Offers
          </a>
          <a href={`tel:+91${config.support_phone || "7719264029"}`} data-testid="header-support-phone" className="hidden 2xl:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 hover:bg-orange-50 hover:text-[#EC5B24]">
            <Headphones className="h-3.5 w-3.5" /> {config.support_phone || "7719264029"}
          </a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-200 pl-1 pr-3 py-1 hover:border-[#EC5B24] transition-colors">
                {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> :
                  <span className="h-7 w-7 rounded-full bg-[#1C1A4A] text-white grid place-items-center text-xs">{user.name?.[0]}</span>}
                <span className="hidden sm:inline text-sm font-medium max-w-[110px] truncate">{user.name?.split(" ")[0]}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem data-testid="menu-my-trips" onClick={() => navigate("/trips")}><Briefcase className="h-4 w-4 mr-2" /> My Trips</DropdownMenuItem>
                {user.is_admin && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><LayoutDashboard className="h-4 w-4 mr-2" /> Bookings desk</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut className="h-4 w-4 mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button data-testid="header-login-btn" onClick={login}
              className="whitespace-nowrap rounded-full bg-[#EC5B24] hover:bg-[#D84A15] text-white text-sm font-semibold px-4 sm:px-5 py-2 transition-colors">
              Log in<span className="hidden sm:inline"> / Sign up</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  const { config } = useAuth();
  const phone = config.support_phone || "7719264029";
  return (
  <footer className="bg-[#12102E] text-slate-300 mt-20" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 py-12 grid gap-8 md:grid-cols-4">
      <div data-testid="company-details">
        <img src="/logo-full-white.png" alt="WanderNest Tour and Travel" className="h-24 w-auto" data-testid="footer-logo" />
        <p className="text-sm mt-3 text-slate-400">Live flight fares, real people booking your tickets. Pay easily by UPI.</p>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex gap-2"><MapPin className="h-4 w-4 text-[#EC5B24] shrink-0 mt-0.5" /> <span data-testid="company-address">{config.address || "Jaigaon, District Alipurduar, West Bengal, India"}</span></div>
          <a href={`tel:+91${phone}`} className="flex gap-2 hover:text-white" data-testid="company-phone"><Phone className="h-4 w-4 text-[#EC5B24]" /> +91 {phone}</a>
          <a href={`https://wa.me/91${phone}`} target="_blank" rel="noopener noreferrer" className="flex gap-2 hover:text-white" data-testid="company-whatsapp"><MessageCircle className="h-4 w-4 text-[#EC5B24]" /> WhatsApp us</a>
        </div>
      </div>
      {[
        ["Company", ["About us", "Careers", "Press", "Investor relations"]],
        ["Support", ["Help centre", "Cancellation", "Refund status", "Contact us"]],
        ["Products", ["Flights", "Holiday packages", "Group booking", "Train, bus & hotel enquiry"]],
      ].map(([h, items]) => (
        <div key={h}>
          <div className="text-white font-semibold mb-3">{h}</div>
          <ul className="space-y-2 text-sm text-slate-400">{items.map((i) => <li key={i}>{i}</li>)}</ul>
        </div>
      ))}
    </div>
    <div className="border-t border-white/10 py-4 text-center text-xs text-slate-500">© 2026 WanderNest · Jaigaon, Alipurduar, West Bengal · All rights reserved.</div>
  </footer>
  );
};
